import { useMemo } from 'react';
import { ProductPricesType, ProductPriceType } from 'types/price';

export type VirtualSetProductPrice = {
    price: ProductPriceType;
    prices: ProductPricesType;
};

export const useVirtualSetCalculatedPrices = (
    productPrices: VirtualSetProductPrice[],
): { finalPrice: VirtualSetProductPrice; hasActionPrice: boolean; hasTescomaPrice: boolean } => {
    return useMemo(() => {
        const finalPrice: VirtualSetProductPrice = {
            price: {
                priceWithVat: 0,
                priceWithoutVat: 0,
                vatAmount: 0,
                currencyCode: '',
                isPriceFrom: false,
                validFrom: null,
                validTo: null,
                b2bRecommendedPrice: null,
            },
            prices: {
                basic: {
                    priceWithVat: 0,
                    priceWithoutVat: 0,
                    vatAmount: 0,
                    currencyCode: '',
                    isPriceFrom: false,
                    validFrom: null,
                    validTo: null,
                    b2bRecommendedPrice: null,
                },
                special: null,
                tescoma: null,
            },
        };

        let hasActionPrice = false;
        let hasTescomaPrice = false;

        for (const product of productPrices) {
            finalPrice.price.priceWithVat += product.price.priceWithVat;
            finalPrice.price.priceWithoutVat += product.price.priceWithoutVat;
            finalPrice.price.vatAmount += product.price.vatAmount;
            finalPrice.price.currencyCode = product.price.currencyCode;

            finalPrice.prices.basic!.priceWithVat += product.prices.basic.priceWithVat;
            finalPrice.prices.basic!.priceWithoutVat += product.prices.basic.priceWithoutVat;
            finalPrice.prices.basic!.vatAmount += product.prices.basic.vatAmount;
            finalPrice.prices.basic!.currencyCode = product.prices.basic.currencyCode;

            if (product.price.priceWithVat < product.prices.basic.priceWithVat) {
                hasActionPrice = true;

                if (!finalPrice.prices.special) {
                    finalPrice.prices.special = {
                        ...product.price,
                        priceWithVat: product.prices.basic.priceWithVat - product.price.priceWithVat,
                        priceWithoutVat: product.prices.basic.priceWithoutVat - product.price.priceWithoutVat,
                        vatAmount: product.prices.basic.vatAmount - product.price.vatAmount,
                        isPriceFrom: false,
                        isActionPrice: true,
                        validFrom: null,
                        validTo: null,
                        b2bRecommendedPrice: null,
                    };
                } else {
                    finalPrice.prices.special.priceWithVat +=
                        product.prices.basic.priceWithVat - product.price.priceWithVat;
                    finalPrice.prices.special.priceWithoutVat +=
                        product.prices.basic.priceWithoutVat - product.price.priceWithoutVat;
                    finalPrice.prices.special.vatAmount += product.prices.basic.vatAmount - product.price.vatAmount;
                }
            }

            const tescomaPriceSource = product.prices.tescoma ?? product.price;
            hasTescomaPrice = hasTescomaPrice || product.prices.tescoma !== null;

            if (!finalPrice.prices.tescoma) {
                finalPrice.prices.tescoma = { ...tescomaPriceSource };
            } else {
                finalPrice.prices.tescoma.priceWithVat += tescomaPriceSource.priceWithVat;
                finalPrice.prices.tescoma.priceWithoutVat += tescomaPriceSource.priceWithoutVat;
                finalPrice.prices.tescoma.vatAmount += tescomaPriceSource.vatAmount;
            }
        }

        return { finalPrice, hasActionPrice, hasTescomaPrice };
    }, [productPrices]);
};
